import { useEffect } from "react";
import { useBrand } from "../providers/BootstrapProvider";

/**
 * BrandCssInjector — Phase 71.
 *
 * Writes 3 CSS variables (--brand-primary, --brand-secondary,
 * --brand-accent) on :root AND injects a high-specificity stylesheet
 * that remaps the existing DHDH plum / cream / coral hex usages (both
 * Tailwind-utility and arbitrary-value) to the founder-selected
 * colours. This gives one-click platform-wide brand swap without
 * rebuilding Tailwind.
 */

const INJECTOR_STYLE_ID = "dhdh-brand-overrides";

// All hex colours DHDH uses in-code that should map to the primary.
const PRIMARY_ALIASES = ["#6B4E8A", "#5A4075", "#4A3360", "#8B6FA8"];
const SECONDARY_ALIASES = ["#FDF8F5", "#FAF7F2"];
const ACCENT_ALIASES = ["#E07A5F", "#C96A52"];

const _escapeHex = (hex) => hex.toLowerCase();

const buildCss = ({ primary_color, secondary_color, accent_color }) => {
  // Attribute selectors catch inline-style usage like style="color:#6B4E8A".
  const rules = [];

  rules.push(`:root {
  --brand-primary: ${primary_color};
  --brand-secondary: ${secondary_color};
  --brand-accent: ${accent_color};
}`);

  // Tailwind utility override: common plum classes used throughout the app.
  // (A full Tailwind palette swap would require a rebuild; this covers the
  // high-signal classes used across the UI: buttons, links, headings.)
  rules.push(`
  .text-plum-500, .text-plum-600, .text-plum-700, .text-plum-800,
  .text-\\[\\#6B4E8A\\], .text-\\[\\#5A4075\\], .text-\\[\\#4A3360\\] {
    color: ${primary_color} !important;
  }
  .bg-plum-500, .bg-plum-600, .bg-plum-700, .bg-plum-800,
  .bg-\\[\\#6B4E8A\\], .bg-\\[\\#5A4075\\], .bg-\\[\\#4A3360\\] {
    background-color: ${primary_color} !important;
  }
  .border-plum-500, .border-plum-600, .border-plum-700,
  .border-\\[\\#6B4E8A\\], .border-\\[\\#5A4075\\] {
    border-color: ${primary_color} !important;
  }
  .ring-plum-500, .ring-plum-600, .ring-plum-700 {
    --tw-ring-color: ${primary_color} !important;
  }
  .hover\\:bg-plum-700:hover, .hover\\:bg-\\[\\#5A4075\\]:hover {
    background-color: ${primary_color} !important;
  }
  .hover\\:text-plum-700:hover, .hover\\:text-\\[\\#6B4E8A\\]:hover {
    color: ${primary_color} !important;
  }
  `);

  // Secondary (cream) — surface backgrounds
  rules.push(`
  .bg-cream, .bg-cream-50, .bg-cream-100,
  .bg-\\[\\#FDF8F5\\], .bg-\\[\\#FAF7F2\\] {
    background-color: ${secondary_color} !important;
  }
  `);

  // Accent (coral)
  rules.push(`
  .text-coral, .text-\\[\\#E07A5F\\], .text-\\[\\#C96A52\\] {
    color: ${accent_color} !important;
  }
  .bg-coral, .bg-\\[\\#E07A5F\\], .bg-\\[\\#C96A52\\] {
    background-color: ${accent_color} !important;
  }
  .border-\\[\\#E07A5F\\] { border-color: ${accent_color} !important; }
  `);

  return rules.join("\n");
};

export const BrandCssInjector = () => {
  const brand = useBrand();

  useEffect(() => {
    let el = document.getElementById(INJECTOR_STYLE_ID);
    if (!el) {
      el = document.createElement("style");
      el.id = INJECTOR_STYLE_ID;
      document.head.appendChild(el);
    }
    el.textContent = buildCss(brand);
    // Also update inline CSS vars on :root so new code can reference them.
    const root = document.documentElement;
    root.style.setProperty("--brand-primary", brand.primary_color);
    root.style.setProperty("--brand-secondary", brand.secondary_color);
    root.style.setProperty("--brand-accent", brand.accent_color);
    // Favicon swap when a custom logo is uploaded.
    if (brand.logo_url && brand.logo_url !== "/dhdh-logo.png") {
      let link = document.querySelector("link[rel='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = brand.logo_url;
    }
  }, [brand.primary_color, brand.secondary_color, brand.accent_color, brand.logo_url]);

  return null;
};
